import { useAuth } from "@clerk/clerk-react";
import { OrganizationUserContext } from "contexts/Organization";
import { useCallback, useContext, useRef, useState } from "react";

import { fetcherAuth } from "utils/api";

import {
  PersonalizeRequestPayload,
  STEP_LABELS,
  StepKey,
  StepState,
  StepStatus,
} from "./schema";

type State = {
  running: boolean;
  steps: StepState[];
  deltaHtml: string;
  finalHtml: string | null;
  errorMessage: string | null;
};

const initial: State = {
  running: false,
  steps: [],
  deltaHtml: "",
  finalHtml: null,
  errorMessage: null,
};

export function useStreamingPersonalize() {
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);
  const [state, setState] = useState<State>(initial);
  const abortRef = useRef<AbortController | null>(null);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setState(initial);
  }, []);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setState((prev) => ({ ...prev, running: false }));
  }, []);

  const run = useCallback(
    async (payload: PersonalizeRequestPayload, plannedSteps: StepKey[]) => {
      if (!currentOrg?.id) return;

      // Initialize steps in pending state in the order they'll execute.
      setState({
        running: true,
        steps: plannedSteps.map((key) => ({
          key,
          label: STEP_LABELS[key],
          status: "pending",
        })),
        deltaHtml: "",
        finalHtml: null,
        errorMessage: null,
      });

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const res: Response = await fetcherAuth(
          getToken,
          `/api/organization/${currentOrg.id}/recommendations/personalize`,
          "POST",
          {},
          payload,
          true,
          false,
          true,
          controller.signal,
        );

        if (!res || !res.ok || !res.body) {
          throw new Error("Failed to start personalize stream.");
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          const events = buffer.split("\n\n");
          buffer = events.pop() ?? "";

          for (const evt of events) {
            const dataLine = evt
              .split("\n")
              .find((line) => line.startsWith("data:"));
            if (!dataLine) continue;
            let evtPayload: {
              type: string;
              text?: string;
              html?: string;
              step?: StepKey;
              status?: StepStatus;
              message?: string;
            };
            try {
              evtPayload = JSON.parse(dataLine.slice(5).trim());
            } catch {
              continue;
            }

            if (evtPayload.type === "progress") {
              const { step, status, message } = evtPayload as {
                step: StepKey;
                status: StepStatus;
                message?: string;
              };
              setState((prev) => ({
                ...prev,
                steps: prev.steps.map((s) =>
                  s.key === step ? { ...s, status, errorMessage: message } : s,
                ),
              }));
            } else if (evtPayload.type === "delta") {
              setState((prev) => ({
                ...prev,
                deltaHtml: prev.deltaHtml + (evtPayload.text ?? ""),
              }));
            } else if (evtPayload.type === "done") {
              setState((prev) => ({
                ...prev,
                running: false,
                finalHtml: evtPayload.html ?? prev.deltaHtml,
                steps: prev.steps.map((s) =>
                  s.key === "draft" ? { ...s, status: "done" } : s,
                ),
              }));
            } else if (evtPayload.type === "error") {
              const message: string =
                evtPayload.message ?? "Something went wrong.";
              const errStep = evtPayload.step;
              setState((prev) => ({
                ...prev,
                running: false,
                errorMessage: message,
                steps: errStep
                  ? prev.steps.map((s) =>
                      s.key === errStep
                        ? { ...s, status: "error", errorMessage: message }
                        : s,
                    )
                  : prev.steps,
              }));
            }
          }
        }
      } catch (e) {
        if ((e as Error).name === "AbortError") return;
        setState((prev) => ({
          ...prev,
          running: false,
          errorMessage:
            (e as Error).message || "Personalize request failed. Try again.",
        }));
      } finally {
        abortRef.current = null;
      }
    },
    [currentOrg?.id, getToken],
  );

  return { ...state, run, cancel, reset };
}
